import { Box, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import FormMonitoring from "components/FormMonitoring";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppColors } from "resources/AppColors";
import CustomDialog from "components/Dialogs/MasterDialog";
import { useLocation, useNavigate } from "react-router-dom";
import { CommonErrorsTexts, organizationsTexts } from "locale/es";
import { z } from "zod";
import { Controller, useController, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FileInput from "components/FileInput";
import { encodeImageFileAsURL } from "utils/HelperFunctions";

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const claimType = z.string().superRefine((val, ctx) => {
  if (val.length === 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 2,
      type: "string",
      inclusive: true,
      message: organizationsTexts.formInputErrors.description,
    });
  }

  return val;
});

const newOrganizationSchema = z
  .object({
    name: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, organizationsTexts.formInputErrors.name),
    claim: claimType,
    merchant: z.string(),
    logo: z.any(),
    logoContentType: z.string().optional(),
    merchantLogo: z.any(),
    merchantLogoContentType: z.string().optional(),
  })
  .transform((data) => ({
    ...data,
    id: null,
  }));

function OrganizationForm({ onSubmit, isEdit }) {
  const { entity } = useLocation().state;

  const entityId = entity?.id;

  const navigate = useNavigate();

  const classes = useStyles();

  const dispatch = useDispatch();

  const { handleSubmit, control, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(newOrganizationSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      name: entity?.name || "",
      claim: entity?.claim || "",
      merchant: entity?.merchant || "",
      logo: entity?.logo || null,
      logoContentType: entity?.logoContentType || "",
      merchantLogo: entity?.merchantLogo || null,
      merchantContentType: entity?.merchantContentType || "",
    },
  });

  const {
    field: { value: logo, onChange: changeLogo },
  } = useController({
    name: "logo",
    control,
  });

  const {
    field: { value: logoContentType, onChange: changeLogoContentType },
  } = useController({
    name: "logoContentType",
    control,
  });

  const {
    field: { value: merchantLogo, onChange: changeMerchantLogo },
  } = useController({
    name: "merchantLogo",
    control,
  });

  const {
    field: {
      value: merchantLogoContentType,
      onChange: changeMerchantLogoContentType,
    },
  } = useController({
    name: "logoContentType",
    control,
  });

  const [urlLogo] = useState(entity?.logoUrl || null);
  const [urlMerchantLogo] = useState(entity?.merchantLogoUrl || null);
  const [formError, setFormError] = useState("");
  const [resetImage, setResetImage] = useState(false);

  async function handleOnConfirmSubmit(data) {
    try {
      const organizationsObject = {
        ...data,
        id: isEdit ? entityId : null,
      };
      if (isEdit) {
        await dispatch(onSubmit(entityId, organizationsObject));
      } else {
        await dispatch(onSubmit(organizationsObject));
      }
      handleShowAddModal();
    } catch (error) {
      setFormError(error);
    }
  }

  function resetFormInputs() {
    resetImageAction();
    reset();
  }

  function resetImageAction() {
    setResetImage(!resetImage);
  }

  function handleLogo(e) {
    changeLogoContentType(e.target.value.type);
    encodeImageFileAsURL(e.target.value, (result) => {
      changeLogo(result?.replace(/^data:image\/[a-z]+;base64,/, ""));
    });
  }

  function handleMerchantLogo(e) {
    changeMerchantLogoContentType(e.target.value.type);
    encodeImageFileAsURL(e.target.value, (result) => {
      changeMerchantLogo(result?.replace(/^data:image\/[a-z]+;base64,/, ""));
    });
  }

  function handleShowAddModal() {
    navigate("/organizaciones");
  }

  return (
    <CustomDialog
      title={isEdit ? organizationsTexts.EDIT : organizationsTexts.CREATE}
      show={true}
      handleShow={handleShowAddModal}
    >
      <FormMonitoring
        submitForm={handleSubmit(handleOnConfirmSubmit)}
        resetFormInputs={resetFormInputs}
        isEdit={isEdit}
      >
        <Controller
          control={control}
          name='name'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              label={organizationsTexts.NAME}
              margin='normal'
              variant='outlined'
              multiline
              autoFocus
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <Controller
          control={control}
          name='claim'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              label={organizationsTexts.CLAIM}
              margin='normal'
              variant='outlined'
              multiline
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <Box sx={{ mb: -2 }}>
          <FileInput
            label={organizationsTexts.LOGO}
            existingFile={urlLogo ? urlLogo : logo}
            onChange={handleLogo}
            reset={resetImage}
            fileType={logoContentType}
          />
        </Box>
        <Controller
          control={control}
          name='merchant'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              label={organizationsTexts.MERCHANT}
              margin='normal'
              variant='outlined'
              multiline
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <FileInput
          label={organizationsTexts.MERCHANTLOGO}
          existingFile={urlMerchantLogo ? urlMerchantLogo : merchantLogo}
          onChange={handleMerchantLogo}
          reset={resetImage}
          fileType={merchantLogoContentType}
        />
        <Typography variant='body' sx={{ color: "red" }}>
          {formError && organizationsTexts.formInputErrors.api_error}
        </Typography>
      </FormMonitoring>
    </CustomDialog>
  );
}

export default OrganizationForm;

